import React, { useMemo, useState } from 'react'
import { Maybe } from '@graphql-tools/utils'
import axios from 'axios'
import classNames from 'classnames'
import { setCookie, getCookie, deleteCookie } from 'cookies-next'
import { Controller, SubmitHandler, useForm, UseFormRegister } from 'react-hook-form'
import { Else, If, Then, When } from 'react-if'
import { Button } from '@/atoms/Button'
import { FlexColumn, FlexRow } from '@/atoms/FlexContainers'
import { CheckIcon } from '@/atoms/Icons/CheckIcon'
import { LoadingSpinnerIcon } from '@/atoms/Icons/LoadingSpinnerIcon'
import { VisibilityOffIcon } from '@/atoms/Icons/VisibilityOffIcon'
import { VisibilityOnIcon } from '@/atoms/Icons/VisibilityOnIcon'
import { DateInput } from '@/atoms/Input/DateInput'
import { Select } from '@/atoms/Input/Select'
import { InternalLink } from '@/atoms/InternalLink'
import { PaddedContainer } from '@/atoms/PaddedContainer'
import { CaptionSM, CaptionXS, LabelLG, TitleLG } from '@/atoms/Text'
import { LOGIN_USER_EMAIL } from '@/constants/cookies'
import { GIFTED_GUILD_FLOW, GIFTED_INVITATION_ID, LOGIN_REDIRECT_URL } from '@/constants/cookies'
import { GUILD_SIGNUP_OFFER_ID_COOKIE, GUILD_SIGNUP_PROMO_CODE_COOKIE } from '@/constants/cookies'
import { paths } from '@/constants/paths'
import { useLegacyEffect } from '@/molecules/utils'
import { AuthApi } from '@/services/Auth/AuthService'
import { useLegalDocumentsByClient } from '@/services/EllisIslandService/legalDocumentHooks'
import { buildLoginUrl } from '@/utils/BuildLoginUrl'
import { useSafeTrack } from '@/utils/analytics'
import { logger } from '@/utils/logging'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { useTranslate } from '@/utils/translate/translate-client'
import { LoginOrigin } from '@/views/GuildJoinView/GuildEmailInput/GuildEmailInput'

interface IFormValues {
  email: string
  password: string
  legalDocuments: { [key: string]: boolean } | null
  verificationCode: string
  verifyPassword?: string
  dateOfBirth?: string | null
  sex?: string | null
}

interface InputProps {
  name: 'email' | 'password' | 'verifyPassword' | 'verificationCode'
  label: string
  register: UseFormRegister<IFormValues>
  required?: { value: boolean; message: string } | boolean
  pattern?: { value: RegExp; message: string }
  validate?: (value: string | undefined) => boolean | string
  placeholder?: string
  type?: string
  hideLabel?: boolean
  error?: string
  showPassword?: boolean
  onTogglePassword?: () => void
}

export type AuthStep = 'email' | 'create-account'

type SignUpAuthFormProps = {
  step: AuthStep
  userEmail: string
  offerId?: string
  loginStartedOrigin?: Maybe<LoginOrigin>
  contentShareId?: string | null
  episodeLink?: string | null
  loginRedirectUrl?: Maybe<string>
} & ModalStateTypes

type ModalStateTypes =
  | {
      isModal?: true
      onEmailSubmit: () => void
    }
  | {
      isModal?: false
      onEmailSubmit?: never
    }

const Input: React.FC<InputProps> = ({
  label,
  name,
  register,
  required,
  pattern,
  validate,
  error,
  showPassword,
  onTogglePassword,
  ...rest
}) => {
  const isPasswordField = name === 'password' || name === 'verifyPassword'
  const registration = validate
    ? register(name, { required, pattern, validate })
    : register(name, { required, pattern })
  return (
    <div className="flex flex-col">
      <label className="photon-label-sm pb-1.5 font-whitney font-medium">{label}</label>
      <div className="relative">
        <input
          {...registration}
          {...rest}
          type={isPasswordField && showPassword ? 'text' : rest.type}
          aria-invalid={error ? 'true' : 'false'}
          className={classNames(
            'photon-paragraph-md border border-b rounded-lg p-2 w-full !duration-[0ms] focus-visible:ring-transparent focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:ring-offset-transparent disabled:bg-white',
            error ? 'border-error-500' : 'border-core-gray-300',
            isPasswordField ? 'pr-10' : '',
          )}
        />
        {isPasswordField && onTogglePassword && (
          <button
            type="button"
            tabIndex={0}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-core-gray-500 focus:outline-none hover:text-core-gray-900"
            onClick={onTogglePassword}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? (
              <div className="pr=[2px]">
                <VisibilityOffIcon color="core-gray-500" />
              </div>
            ) : (
              <VisibilityOnIcon color="core-gray-500" />
            )}
          </button>
        )}
      </div>
      <CaptionXS role="alert" className="min-h-[20px]">
        {error ? <span className="text-error-500">{error || 'An error occurred'}</span> : <span>&nbsp;</span>}
      </CaptionXS>
    </div>
  )
}

// TODO: on create-account step, need to add path for edge case where user with account some how makes it to that step and enters the wrong password
export const SignUpAuthForm: React.FC<SignUpAuthFormProps> = ({
  step,
  userEmail,
  isModal,
  offerId,
  onEmailSubmit,
  loginStartedOrigin,
  contentShareId,
  episodeLink,
  loginRedirectUrl,
}) => {
  const [error, setError] = useState('')
  const [resendStatus, setResendStatus] = useState<'idle' | 'success' | 'sending'>('idle')
  const { t } = useTranslate('guild')
  const { asPath } = useSafeRouter()
  const { requiredLegalDocs, optionalLegalDocs } = useLegalDocumentsByClient()
  const authService = new AuthApi()
  const [emailNeedsVerification, setEmailNeedsVerification] = useState(step === 'create-account')
  const [isEditingEmail, setIsEditingEmail] = useState(false)
  const isContentShareOrigin = loginStartedOrigin === 'shared'

  const {
    register,
    formState: { errors, isSubmitting },
    handleSubmit,
    getValues,
    control,
    trigger,
  } = useForm<IFormValues>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    shouldFocusError: true,
    defaultValues: {
      email: userEmail,
      password: '',
      legalDocuments: null,
      verificationCode: '',
      verifyPassword: '',
      dateOfBirth: null,
      sex: null,
    },
  })

  const [showPassword, setShowPassword] = useState(false)
  const [showVerifyPassword, setShowVerifyPassword] = useState(false)
  const queryString = asPath.includes('?') ? asPath.split('?')[1]! : ''
  const queryStringWithDelimiter = (symbol: '?' | '&') => (queryString === '' ? '' : `${symbol}${queryString}`)
  const track = useSafeTrack()

  const router = useSafeRouter()

  useLegacyEffect(() => {
    if (isModal) {
      if (step === 'email') track('Guild Sign Up Flow Started')
      if (step === 'create-account') track('Guild Sign Up Flow Create Password Started')
    }
  }, [track, step, isModal])

  const isCreateAccountStep = step === 'create-account'
  function getLegalDocumentsArray(legalDocuments: { [key: string]: boolean } | null) {
    const optionalDocsArray = legalDocuments ? Object.keys(legalDocuments).filter((key) => !!legalDocuments[key]) : []
    const requiredDocsArray = requiredLegalDocs.map((doc) => doc.id)
    return [...requiredDocsArray, ...optionalDocsArray]
  }

  // Gifted flow handling
  const isGiftedFlow = useMemo(() => {
    try {
      const qs = new URLSearchParams(queryString)
      const giftedParam = qs.get('gifted')
      const giftedCookie = getCookie(GIFTED_GUILD_FLOW)
      return giftedParam === '1' || giftedCookie === '1'
    } catch {
      const giftedCookie = getCookie(GIFTED_GUILD_FLOW)
      return giftedCookie === '1'
    }
  }, [queryString])

  const getGiftedRedirectUrl = () => {
    // Prefer explicit redirect cookie if it already points to the invite URL
    let cookieRedirect = (getCookie(LOGIN_REDIRECT_URL) as string | undefined) || ''
    if (cookieRedirect.includes('/account/guild-membership/invite/')) {
      // Ensure claim=1 is present so we don't restart the flow
      if (!cookieRedirect.includes('claim=1')) {
        const hasQuery = cookieRedirect.includes('?')
        cookieRedirect = `${cookieRedirect}${hasQuery ? '&' : '?'}claim=1`
        setCookie(LOGIN_REDIRECT_URL, cookieRedirect)
      }
      return cookieRedirect
    }

    // Otherwise build from invitation cookie
    const inviteId = getCookie(GIFTED_INVITATION_ID) as string | undefined
    if (inviteId && typeof inviteId === 'string' && inviteId.length > 0) {
      const invitePath = paths.account.guildMembershipInvite(inviteId, true)
      // Ensure cookie is set for downstream login redirects
      if (!cookieRedirect) setCookie(LOGIN_REDIRECT_URL, invitePath)
      return invitePath
    }

    // Fallback to account guild membership root if no invite id found
    const fallback = paths.account.guildMembership
    if (!cookieRedirect) setCookie(LOGIN_REDIRECT_URL, fallback)
    return fallback
  }

  function getSignupRedirectUrl() {
    // Gifted flow should always return to invite page
    if (isGiftedFlow) {
      return getGiftedRedirectUrl()
    }
    if (isContentShareOrigin && contentShareId) {
      return `${paths.watch.shared}/${contentShareId}`
    }
    if (episodeLink) {
      return episodeLink
    }
    if (loginStartedOrigin === 'mercy-ships-multi-claim') {
      return '/m/shared/mercy-ships'
    }
    if (loginRedirectUrl) {
      return loginRedirectUrl
    }
    return paths.guild.signup.plan
  }

  const handleSignup = async ({
    email,
    password,
    dateOfBirth,
    sex,
    legalDocuments,
  }: {
    email: string
    password: string
    dateOfBirth?: string | null
    sex?: string | null
    legalDocuments: { [key: string]: boolean } | null
  }) => {
    track('Guild Sign Up Flow Attempt Account Creation')
    const userCheck = await fetch(`/api/authByApi/checkUser?email=${encodeURIComponent(email)}`)
    const userCheckJson = await userCheck.json()
    if (userCheckJson.exists) {
      const ellisOauthUrl = buildLoginUrl(getSignupRedirectUrl())
      router.push(ellisOauthUrl)
      return
    }

    await axios
      .post('/api/authByApi/signUp', {
        email: email.trim(),
        password,
        legalDocuments: getLegalDocumentsArray(legalDocuments),
        ...(dateOfBirth ? { dateOfBirth } : {}),
        ...(sex ? { sex } : {}),
      })
      .then((res) => {
        if (res.data.status === 'Success') {
          track('Guild Signup Account Created', { email })
          const offerId = getCookie(GUILD_SIGNUP_OFFER_ID_COOKIE)
          if (offerId) {
            const promoCode = getCookie(GUILD_SIGNUP_PROMO_CODE_COOKIE)
            deleteCookie(GUILD_SIGNUP_OFFER_ID_COOKIE)
            deleteCookie(GUILD_SIGNUP_PROMO_CODE_COOKIE)
            let checkoutUrl = `${paths.guild.checkout}/${offerId}`
            if (promoCode) {
              checkoutUrl += `?promoCode=${promoCode}`
            }
            router.push(checkoutUrl)
            return
          }

          const urlString = getSignupRedirectUrl()
          const shouldAppendQueryString = urlString === paths.guild.signup.plan
          router.push(`${urlString}${shouldAppendQueryString ? queryStringWithDelimiter('?') : ''}`)
        }
      })
      .catch((e) => {
        logger().error('Error creating account', e)
        setError(e?.response?.data?.message || 'An error occurred, please try again or contact support')
      })
  }

  const handleVerifyEmail = async ({ email, verificationCode }: { email: string; verificationCode: string }) => {
    try {
      const authResponse = await authService.submitVerificationCode(email, verificationCode)
      if (authResponse.status === 200) {
        setEmailNeedsVerification(false)
      }
    } catch (e) {
      setError(t('invalidCode', 'Invalid code'))
    }
  }

  const handleResendVerificationCode = async ({ email }: { email: string }) => {
    setResendStatus('sending')
    try {
      await authService.sendVerificationCode(email)
      setResendStatus('success')
    } catch (e) {
      setError(t('errorSendingVerificationCode', 'Error sending verification code. Please try again.'))
    }
  }

  const handleEditEmailClick = () => {
    setIsEditingEmail(true)
  }

  const onSubmit: SubmitHandler<IFormValues> = async ({
    email,
    password,
    dateOfBirth,
    sex,
    legalDocuments,
    verificationCode,
  }) => {
    setError('')
    if (isCreateAccountStep) {
      if (!emailNeedsVerification) {
        if (dateOfBirth && sex) {
          handleSignup({ email, password, dateOfBirth, sex, legalDocuments })
        } else {
          handleSignup({ email, password, legalDocuments })
        }
      } else {
        handleVerifyEmail({ email, verificationCode })
      }
    } else {
      const userCheck = await axios.get(`/api/authByApi/checkUser?email=${encodeURIComponent(email)}`)
      if (userCheck?.data?.exists === true) {
        track('Guild Sign Up Flow User Exists')
        const urlString = getUrlString({ isContentShareOrigin, contentShareId, offerId, episodeLink, isModal: true })
        const url = buildLoginUrl(urlString)
        return router.push(url)
      }

      authService.sendVerificationCode(email)
      track('Guild Sign Up Flow Email Submitted')
      if (!isModal) {
        const urlString = getUrlString({ isContentShareOrigin, contentShareId, offerId, isModal: false })
        setCookie(LOGIN_USER_EMAIL, email)
        router.push(urlString)
      } else {
        onEmailSubmit()
      }
    }
  }

  const getUrlString = ({
    isContentShareOrigin,
    contentShareId,
    offerId,
    episodeLink,
    isModal,
  }: {
    isContentShareOrigin: boolean
    contentShareId?: string | null
    offerId?: string
    episodeLink?: string | null
    isModal: boolean
  }) => {
    if (isGiftedFlow) {
      return getGiftedRedirectUrl()
    }
    if (isContentShareOrigin && contentShareId) {
      return `${paths.watch.shared}/${contentShareId}`
    }
    if (episodeLink) {
      return episodeLink
    }
    if (offerId) {
      return isModal ? `${paths.guild.checkout}/${offerId}` : `${paths.guild.signup.createAccount}/${offerId}`
    }
    return isModal ? paths.guild.pricing : paths.guild.signup.createAccount
  }

  const stepTitle = {
    email: t('enterYourEmailAddressToSetUpAccount', 'Enter your email address to set up your account'),
    'create-account': emailNeedsVerification
      ? t('verifyYourEmail', 'Verify your email')
      : isContentShareOrigin
      ? t('createPasswordStartWatching', 'Create a password to start watching')
      : t('createAPassswordToStartMembership', 'Create a password to start your membership'),
  }

  const buttonText = {
    email: t('continue', 'Continue'),
    'create-account': t('next', 'Next'),
  }

  const { sexOptions } = useMemo(() => {
    const sexOpts = [
      { display: t('male', 'Male'), value: 'male' },
      { display: t('female', 'Female'), value: 'female' },
      { display: t('preferNotToSay', 'Prefer Not To Say'), value: 'prefer_not_to_say' },
    ]
    return { sexOptions: sexOpts }
  }, [t])

  return (
    <PaddedContainer className="min-h-[546px] !p-8 sm:w-[440px]">
      <FlexColumn>
        <When condition={!isContentShareOrigin}>
          <CaptionSM className="mb-4 font-thin tracking-widest">
            STEP {emailNeedsVerification ? '1' : '2'} OF 2
          </CaptionSM>
        </When>
        <div className="pb-2">
          <TitleLG>{stepTitle[step]}</TitleLG>
        </div>
        <When condition={isEditingEmail}>
          <CaptionSM className="mb-7 leading-5">
            {isContentShareOrigin
              ? t(
                  'soCloseToWatchingFree',
                  `You're just a few clicks away from watching a light amplifying show -- completely free!`,
                )
              : t(
                  'soCloseFewClicksAway',
                  `You're so close! Just a few clicks away from exploring our exciting library of films and shows!`,
                )}
          </CaptionSM>
        </When>
        {emailNeedsVerification && (
          <CaptionSM className="mb-7 leading-5">
            {t(
              'checkYourInboxForVerificationCode',
              'Check your inbox for a verification code at the email address shown below.',
            )}
          </CaptionSM>
        )}
        <form name="account-form" noValidate onSubmit={handleSubmit(onSubmit)}>
          <FlexColumn className="gap-2">
            <If condition={!isEditingEmail}>
              <Then>
                <div className="mb-4">
                  <When condition={emailNeedsVerification}>
                    <CaptionSM>
                      Welcome <b>{getValues('email')}</b>, verify your email.
                    </CaptionSM>
                  </When>
                  <When condition={!emailNeedsVerification}>
                    <CaptionSM>
                      Welcome <b>{getValues('email')}!</b> Just a few clicks away from exploring our exciting library of
                      films and shows!
                    </CaptionSM>
                  </When>
                  <Button
                    variant="plain"
                    type="button"
                    className="photon-caption-sm self-start text-core-oxide underline"
                    onClick={handleEditEmailClick}
                  >
                    Edit email address
                  </Button>
                </div>
              </Then>
              <Else>
                <Input
                  register={register}
                  name="email"
                  label={t('emailAddress', 'Email address')}
                  required={true}
                  error={errors.email?.message || errors.email ? 'Please enter a valid email address' : undefined}
                />
              </Else>
            </If>

            {isCreateAccountStep && emailNeedsVerification && (
              <>
                <Input
                  register={register}
                  name="verificationCode"
                  label={t('verificationCode', 'Verification code')}
                  required={true}
                  pattern={{
                    value: /[0-9]{6}/,
                    message: t('verificationCodeMustBeSixDigits', 'Verification code must be 6 digits'),
                  }}
                  error={
                    errors.verificationCode?.message ||
                    (errors.verificationCode
                      ? t('verificationCodeMustBeSixDigits', 'Verification code must be 6 digits')
                      : undefined)
                  }
                  placeholder="000000"
                />
                <Button
                  className="photon-caption-sm mb-4 flex items-center gap-1 self-start text-core-oxide underline"
                  variant="plain"
                  type="button"
                  disabled={resendStatus === 'sending'}
                  onClick={() => handleResendVerificationCode({ email: getValues('email') })}
                >
                  Resend verification code {resendStatus === 'success' && <CheckIcon color="core-oxide" />}
                </Button>
              </>
            )}

            {isCreateAccountStep && !emailNeedsVerification && (
              <>
                <Input
                  register={register}
                  name="password"
                  label={t('password', 'Password')}
                  required={true}
                  pattern={{
                    value: /^.{8,}$/i,
                    message: t('passwordMustBeAtLeastEightCharacters', 'Password must be at least 8 characters'),
                  }}
                  type="password"
                  error={
                    errors.password?.message ||
                    (errors.password
                      ? t('passwordMustBeAtLeastEightCharacters', 'Password must be at least 8 characters')
                      : undefined)
                  }
                  showPassword={showPassword}
                  onTogglePassword={() => setShowPassword((v) => !v)}
                />

                <Input
                  register={register}
                  name="verifyPassword"
                  label={t('verifyPassword', 'Verify Password')}
                  required={true}
                  type="password"
                  error={
                    errors.verifyPassword?.message ||
                    (errors.verifyPassword ? t('passwordsDoNotMatch', 'Passwords do not match') : undefined)
                  }
                  validate={(value: string | undefined) =>
                    value === getValues('password') || t('passwordsDoNotMatch', 'Passwords do not match')
                  }
                  pattern={undefined}
                  showPassword={showVerifyPassword}
                  onTogglePassword={() => setShowVerifyPassword((v) => !v)}
                />

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {/* Date of Birth */}
                  <Controller
                    name="dateOfBirth"
                    control={control}
                    rules={{ required: { value: true, message: t('required', 'Required') } }}
                    render={({ field }) => (
                      <DateInput
                        name="dateOfBirth"
                        label={t('dateOfBirth', 'Date of Birth')}
                        value={field.value || ''}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        required={true}
                        max={new Date().toISOString().split('T')[0]}
                        error={
                          errors.dateOfBirth?.message || (errors.dateOfBirth ? t('required', 'Required') : undefined)
                        }
                      />
                    )}
                  />

                  {/* Sex */}
                  <div>
                    <label className="photon-label-sm pb-1.5 font-whitney font-medium">{t('sex', 'Sex')}</label>
                    <Controller
                      name="sex"
                      control={control}
                      rules={{ required: { value: true, message: t('required', 'Required') } }}
                      render={({ field }) => (
                        <>
                          <Select
                            name="sex"
                            value={field.value || null}
                            onChange={(val: string) => {
                              field.onChange(String(val))
                              void trigger('sex')
                            }}
                            onBlur={field.onBlur}
                            options={sexOptions}
                            placeholder={t('select', 'Select')}
                            divClassName="w-full"
                            buttonClassName={classNames('!rounded-lg !h-[48px]', {
                              '!border-error-500': !!errors.sex,
                            })}
                            optionClassName="!pl-8"
                          />
                          <CaptionXS role="alert" className="min-h-[20px]">
                            {errors.sex ? (
                              <span className="text-error-500">{t('required', 'Required')}</span>
                            ) : (
                              <span>&nbsp;</span>
                            )}
                          </CaptionXS>
                        </>
                      )}
                    />
                  </div>
                </div>

                <FlexRow className="gap-2 px-1 pt-7">
                  {requiredLegalDocs?.length > 0 && (
                    <>
                      <CaptionXS className="text-core-gray-500">
                        By proceeding, you acknowledge that you have read and agree to our{' '}
                        {requiredLegalDocs.map((doc, index) => (
                          <span key={doc.id}>
                            <InternalLink key={doc.id} href={doc.source} className="text-link-blue" target="_blank">
                              {doc.title}
                            </InternalLink>
                            {handleLegalDocsPunctuation(requiredLegalDocs.length, index)}
                          </span>
                        ))}
                      </CaptionXS>
                    </>
                  )}
                </FlexRow>
                {optionalLegalDocs?.length > 0 &&
                  optionalLegalDocs.map((doc) => {
                    const id = `legalDocuments-${doc.id}`
                    const docError = (errors.legalDocuments as unknown as Record<string, { message?: string }>)?.[
                      doc.id
                    ]
                    const reg = register(`legalDocuments.${doc.id}`, {
                      required: { value: true, message: t('required', 'Required') },
                    })
                    return (
                      <div key={doc.id} className="px-1 pb-7">
                        <label htmlFor={id} className="flex cursor-pointer items-start gap-2">
                          <input
                            id={id}
                            type="checkbox"
                            aria-required={true}
                            aria-invalid={docError ? 'true' : 'false'}
                            className={classNames('mt-[2px] h-4 w-4 rounded', { 'ring-1 ring-error-500': !!docError })}
                            defaultChecked={false}
                            {...reg}
                            onChange={(e) => {
                              reg.onChange(e)
                              // Immediately revalidate this checkbox so the error clears on check
                              void trigger(`legalDocuments.${doc.id}`)
                            }}
                          />
                          <CaptionXS className="text-core-gray-500">
                            {t('iAgreeTo', 'I have read and agree to the')}{' '}
                            <InternalLink key={doc.id} href={doc.source} className="text-link-blue" target="_blank">
                              {doc.title}
                            </InternalLink>
                          </CaptionXS>
                        </label>
                        <CaptionXS role="alert" className="min-h-[20px]">
                          {docError ? (
                            <span className="text-error-500">{docError.message || t('required', 'Required')}</span>
                          ) : (
                            <span>&nbsp;</span>
                          )}
                        </CaptionXS>
                      </div>
                    )
                  })}
              </>
            )}
            <Button
              variant="core-gray-900"
              type="submit"
              className="min-h-[50px] w-full px-6 py-[12px]"
              disabled={isSubmitting}
            >
              {isSubmitting && <LoadingSpinnerIcon size={18} color="white" />}
              <LabelLG>{!isSubmitting && buttonText[step]}</LabelLG>
            </Button>
            {error && (
              <CaptionXS role="alert">
                <span className="text-error-500">{error}</span>
              </CaptionXS>
            )}
          </FlexColumn>
        </form>
      </FlexColumn>
    </PaddedContainer>
  )
}

const handleLegalDocsPunctuation = (length: number, index: number) => {
  if (length === 0) return ''
  if (length === 1) return '.'
  return index === length - 1 ? '.' : index === length - 2 ? ' and ' : ', '
}
